import {Trans} from '@lingui/macro';
import React, {ReactElement, useCallback, useEffect, useImperativeHandle, useState} from 'react';
import styled from 'styled-components';

import {Handler} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {AsyncStatusesEnum} from '../../../../libs/shared/helpers/src/types/asyncStatuses';
import {VisualSizesEnum} from '../../../../libs/shared/ui/src/styles/commonStyles';
import {usePrevious} from '../../../../srcCommon/helpers/hookHelpers';
import {buildCancelTokenSource, CancelError, CancelTokenSource} from '../../../helpers/asyncCancelHelpers';
import {TablePaginationDataModel} from '../../../models/table/tablePaginationDataModel';
import {AsyncLoader} from '../loaders/asyncLoader';
import {getTableResultsMoreStatuses, TableSource} from './source/tableSource';
import {SelectedColumnSort} from './tableColumn';
import {VirtualTableBase, VirtualTableInheritableProps} from './virtualTableBase';

/*
 * Types.
 */

export interface VirtualTableProps<T> extends VirtualTableInheritableProps<T> {
  source: TableSource;
  paginationData?: TablePaginationDataModel;

  /**
   * @deprecated When the source changes, we force reload the table by default. This requires the source
   * to be stable (See contactsSearchTableContainer.tsx on how to make a stable source.) otherwise we will
   * constantly be issuing reloads.
   */
  isSourceUnstable?: boolean;
}

export type VirtualTableHandle = {
  forceReload: Handler;
};

/*
 * Styles.
 */

const StyledErrorDiv = styled.div`
  pointer-events: all;
  padding: 64px 0;
`;

/*
 * Component.
 */

const VirtualTableComponent = <T extends {}>(props: VirtualTableProps<T>, ref: any) => {
  const {
    columns,
    source,
    sortableColumns,
    sortedColumn,
    bottomPadding,
    size,
    items,
    paginationData,
    shouldAllowMultiSelect,
    isInMultiSelectMode,
    isSourceUnstable,
    renderRow,
    renderEmpty,
    renderMultiSelectHeader,
    onMultiSelectHeaderChange,
  } = props;

  const canLoadMore = paginationData?.canLoadMore ?? true;

  const [cancelTokenSource, setCancelTokenSource] = useState<CancelTokenSource | undefined>(undefined);
  const [isFetching, setIsFetching] = useState(false);
  const [hasErredFetching, setHasErredFetching] = useState(false);
  const previousSource = usePrevious(source);

  const shouldRenderEmptyState = computeShouldRenderEmptyState(items, isFetching, paginationData);

  const startFetch = useCallback(() => {
    const newCancelTokenSource = maybeCancelTokenAndCreateCancelTokenSource(cancelTokenSource);
    setCancelTokenSource(newCancelTokenSource);
    setIsFetching(true);
    return newCancelTokenSource;
  }, [cancelTokenSource]);

  const finishFetch = useCallback(() => {
    setCancelTokenSource(undefined);
    setIsFetching(false);
  }, []);

  const forceReload = useCallback(async () => {
    if (!source.forceReloadAsync) {
      return;
    }
    try {
      const newCancelTokenSource = startFetch();
      await source.forceReloadAsync(newCancelTokenSource.token);
      finishFetch();
      setHasErredFetching(false);
    } catch (error) {
      // Http cancel errors are expected.
      if (error instanceof CancelError) {
        setCancelTokenSource(undefined);
        return;
      }

      console.error(error);
      setHasErredFetching(true);
      setIsFetching(false);
    }
  }, [finishFetch, source, startFetch]);

  const fetchInitialData = useCallback(async () => {
    try {
      const newCancelTokenSource = startFetch();
      await source.loadAsync(newCancelTokenSource.token);
      finishFetch();
    } catch (error) {
      // Http cancel errors are expected.
      if (error instanceof CancelError) {
        setCancelTokenSource(undefined);
        return;
      }
      console.error(error);
      setHasErredFetching(true);
      setIsFetching(false);
    }
  }, [finishFetch, source, startFetch]);

  // If we do not have pagination data we can assume this is the first load and we should fetch data.
  useEffect(() => {
    if (!isFetching && !paginationData && !hasErredFetching) {
      fetchInitialData();
    }
  }, [fetchInitialData, hasErredFetching, isFetching, paginationData]);

  // Watch for source changes and if we should force reload.
  useEffect(() => {
    if (!isSourceUnstable && previousSource && previousSource !== source && paginationData) {
      forceReload();
    }
  }, [source, isSourceUnstable, forceReload, previousSource, paginationData]);

  useEffect(
    () => () => {
      if (cancelTokenSource) {
        cancelTokenSource.cancel();
      }
    },
    [cancelTokenSource],
  );

  const loadMoreAsync = async () => {
    if (!source.loadMoreAsync) {
      return;
    }
    try {
      const newCancelTokenSource = startFetch();
      await source.loadMoreAsync(newCancelTokenSource.token);
      finishFetch();
    } catch (error) {
      // Http cancel errors are expected.
      if (error instanceof CancelError) {
        setCancelTokenSource(undefined);
        return;
      }

      console.error(error);
      setHasErredFetching(true);
      setIsFetching(false);
    }
  };

  useImperativeHandle(ref, () => ({
    forceReload: () => {
      forceReload();
    },
  }));

  return (
    <VirtualTableBase
      columns={columns}
      sortedColumn={sortedColumn}
      sortableColumns={sortableColumns}
      items={items}
      size={size}
      isFetching={isFetching}
      shouldAllowMultiSelect={shouldAllowMultiSelect}
      isInMultiSelectMode={isInMultiSelectMode}
      renderRow={renderRow}
      renderEmpty={renderEmpty}
      renderMultiSelectHeader={renderMultiSelectHeader}
      onMultiSelectHeaderChange={onMultiSelectHeaderChange}
      bottomPadding={bottomPadding}
      shouldRenderEmptyState={shouldRenderEmptyState}
      computeMoreStatuses={() => getTableResultsMoreStatuses(isFetching, canLoadMore)}
      onLoadMore={async () => {
        if (!isFetching && paginationData?.canLoadMore) {
          await loadMoreAsync();
        }
      }}
      hasData={Boolean(paginationData)}
      onSortUpdate={(sortOptions: SelectedColumnSort) => {
        if (!source.updateSort) {
          return;
        }
        source.updateSort(sortOptions);
      }}
      hasErredFetching={hasErredFetching}
      renderErrorFetchingState={() => renderErrorFetchingState(forceReload)}
    />
  );
};
export const VirtualTable = React.forwardRef<VirtualTableHandle, VirtualTableProps<any>>(
  VirtualTableComponent,
) as <T extends {}>(p: VirtualTableProps<T> & {ref?: any}) => ReactElement;

/*
 * Helpers.
 */

function renderErrorFetchingState(forceReload: Handler) {
  return (
    <StyledErrorDiv>
      <AsyncLoader
        size={VisualSizesEnum.LARGE}
        status={AsyncStatusesEnum.FAILED}
        error={<Trans id="virtual-table-error-fetching">Something went wrong.</Trans>}
        onRetryClick={forceReload}
      />
    </StyledErrorDiv>
  );
}

function maybeCancelTokenAndCreateCancelTokenSource(source?: CancelTokenSource) {
  if (source) {
    source.cancel();
  }
  return buildCancelTokenSource();
}

function computeShouldRenderEmptyState(
  items: ReadonlyArray<any> | undefined,
  isFetching: boolean,
  paginationData?: TablePaginationDataModel,
) {
  if (isFetching) {
    return false;
  }
  if (items) {
    return items.length === 0 && Boolean(paginationData);
  }
  return false;
}
